import React from 'react';

import {assert} from 'Asserts';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileMenu from 'file-browser/FileMenu';
import normalizeFolder from 'folders/util/normalizeFolder';
import {ListableItem} from 'search/SearchConstants';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

import DragAndDropSidepanelItemWrapper from './DragAndDropSidepanelItemWrapper';
import SidepanelItem from './SidepanelItem';

export interface TopLevelFolderSidepanelItemProps {
  finderId: string;
  openResult(result: StringIDFolder): void;
  isListing: boolean;
  folder: StringIDFolder & ListableItem;
  isChild: boolean;
  isCompact: boolean;
}

interface State {
  isMenuOpen: boolean;
}

export default class TopLevelFolderSidepanelItem extends React.PureComponent<TopLevelFolderSidepanelItemProps> {
  _isMounted = false;

  override state: State = {
    isMenuOpen: false,
  };

  override componentDidMount(): void {
    this._isMounted = true;
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  private handleOpen = (): void => {
    this.props.openResult(this.props.folder);
  };

  private handleContextMenuClose = (): void => {
    if (this._isMounted) {
      this.setState({isMenuOpen: false});
    }
  };

  private handleShowMenuOptions = (e: ElementMouseEvent): void => {
    assert(this.props.finderId != null, 'Expected a non-null finderId');
    FileMenu.open(e, {
      file: this.props.folder,
      finderId: this.props.finderId,
      isListing: this.props.isListing,
      closeCallback: this.handleContextMenuClose,
    });
    this.setState({isMenuOpen: true});
  };

  renderTitle(): JSX.Element {
    return <span key="name">{this.props.folder.name}</span>;
  }

  renderSubTitle(): React.ReactNode {
    if (
      this.props.isCompact ||
      !this.props.folder.source ||
      !this.props.folder.source.alwaysShowAncestorFolderNames
    ) {
      return '';
    }

    let currentFolder: StringIDFolder = this.props.folder;
    const ancestorNames: Array<string> = [currentFolder.name];
    while (currentFolder.parent) {
      currentFolder = normalizeFolder(currentFolder.parent);
      ancestorNames.push(currentFolder.name);
    }

    ancestorNames.reverse();

    return (
      <Popover
        renderContent={() =>
          ancestorNames.map((folderName: string, index: number, arr: Array<string>) => (
            <span key={index}>
              <BenchlingIcon icon={index === 0 ? ModelTypeConstants.PROJECT : ModelTypeConstants.FOLDER} />{' '}
              {folderName} {index < arr.length - 1 && <BenchlingIcon icon="chevron-right" />}{' '}
            </span>
          ))
        }
        placement={FloatingPlacement.BOTTOM}
      >
        <span>{this.props.folder.source.name}</span>
      </Popover>
    );
  }

  override render(): JSX.Element {
    assert(this.props.finderId != null, 'Expected a non-null finderId');
    return (
      <DragAndDropSidepanelItemWrapper
        canInteract={this.state.isMenuOpen}
        finderId={this.props.finderId}
        result={this.props.folder}
        shouldCaptureDrop={true}
      >
        <SidepanelItem
          active={false}
          className="sidepanelItem--dnd"
          icon={<BenchlingIcon icon={ModelTypeConstants.FOLDER} />}
          isCompact={this.props.isCompact}
          faded={Boolean(this.props.folder.archiveRecord)}
          onClick={this.handleOpen}
          onContextMenu={this.handleShowMenuOptions}
          subTitle={this.renderSubTitle()}
          title={{text: this.props.folder.name, node: this.renderTitle()}}
          tooltipText={undefined}
          isExpandable={false}
          isChild={this.props.isChild}
        />
      </DragAndDropSidepanelItemWrapper>
    );
  }
}
